/* eslint-disable import/extensions */
/* eslint-disable react/no-unstable-nested-components */
/* eslint-disable react/jsx-props-no-spreading */

import { AngleRight, Link } from '@/client/components/icons/ContinuIcons';
import {
  Box,
  Button,
  ButtonGroup,
  Flex,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalOverlay,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
  useDisclosure,
  useTab,
} from '@chakra-ui/react';
import { forwardRef, useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { GoDotFill } from 'react-icons/go';
import Lottie from 'lottie-react';
import oneAssignmentMultipleInstancesAnimation from '@/client/assets/lottie/one-assignment-multiple-instances.json';
import recurringAssignmentsAnimation from '@/client/assets/lottie/recurring-assignments.json';
import reportOnInstancesAnimation from '@/client/assets/lottie/report-on-instances.json';
import { useTranslation } from 'react-i18next';
import welcomeToInstancesAnimation from '@/client/assets/lottie/welcome-to-instances.json';

interface NewAssignmentModalProps {
  openCreateInstanceModal: () => void;
}

export default function NewAssignmentInstanceModal({
  openCreateInstanceModal,
}: NewAssignmentModalProps) {
  const { t } = useTranslation();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const navigate = useNavigate();
  const location = useLocation();

  const [tabIndex, setTabIndex] = useState(0);

  useEffect(() => {
    const isCreate = location.pathname.includes('create');
    const isInstance = location.pathname.includes('instance');

    if (isCreate && isInstance) {
      onOpen();
    }
  }, [location]);

  const CustomTab = forwardRef((props, ref) => {
    const tabProps = useTab({ ...props, ref: ref as React.Ref<HTMLElement> });
    const isSelected = !!tabProps['aria-selected'];

    return (
      <Button variant="ghost" size="xs" padding={1} {...tabProps}>
        <Icon as={GoDotFill} color={isSelected ? 'brand.primary' : 'neutral.100'} />
      </Button>
    );
  });

  const slides = [
    {
      title: t('admin.assignments.instances.welcomeModal.slideOne.title'),
      description: t('admin.assignments.instances.welcomeModal.slideOne.description'),
      content: null,
      lottieFile: welcomeToInstancesAnimation,
    },
    {
      title: t('admin.assignments.instances.welcomeModal.slideTwo.title'),
      description: t('admin.assignments.instances.welcomeModal.slideTwo.description'),
      content: null,
      lottieFile: oneAssignmentMultipleInstancesAnimation,
    },
    {
      title: t('admin.assignments.instances.welcomeModal.slideThree.title'),
      description: t('admin.assignments.instances.welcomeModal.slideThree.description'),
      content: null,
      lottieFile: recurringAssignmentsAnimation,
    },
    {
      title: t('admin.assignments.instances.welcomeModal.slideFour.title'),
      description: t('admin.assignments.instances.welcomeModal.slideFour.description'),
      content: (
        <Flex
          backgroundColor="neutral.0"
          paddingX={6}
          paddingY={4}
          justifyContent="space-between"
          alignItems="center"
          borderRadius="8px"
          _hover={{ cursor: 'pointer' }}
          onClick={() => {
            window.open(
              'https://help.continu.com/hc/en-us/articles/32642640689175-Updated-Assignments-Article-Video-Hub',
              '_blank',
            );
          }}
        >
          <HStack>
            <Icon as={Link} />

            <Text fontSize="14px" fontWeight={600}>
              {t('admin.assignments.instances.welcomeModal.slideFour.readMore')}
            </Text>
          </HStack>

          <Icon as={AngleRight} boxSize={3} marginLeft={4} />
        </Flex>
      ),

      image: '/public/images/report-on-instances.jpg',
    },
  ];

  const handleTabsChange = (index: number) => {
    setTabIndex(index);
  };

  return (
    <Modal size="lg" closeOnOverlayClick={false} isOpen={isOpen} onClose={onClose}>
      <ModalOverlay />

      <ModalContent maxHeight="550px" minHeight="550px" borderRadius="16px">
        <ModalBody paddingY={4}>
          {slides[tabIndex].lottieFile && (
            <Lottie
              style={{ borderRadius: '8px', overflow: 'hidden' }}
              loop={false}
              animationData={slides[tabIndex].lottieFile}
            />
          )}

          {slides[tabIndex].image && (
            <Box
              height={272}
              width="full"
              backgroundColor="brand.primary"
              backgroundImage={`url(${slides[tabIndex].image})`}
              animation="fadeIn 1s ease-in"
              sx={{
                '@keyframes fadeIn': {
                  '0%': { opacity: 0 },
                  '100%': { opacity: 1 },
                },
              }}
              backgroundSize="cover"
            />
          )}

          <Tabs index={tabIndex} tabIndex={tabIndex} onChange={handleTabsChange}>
            <TabList justifyContent="center">
              {slides.map((slide) => (
                <CustomTab key={slide.title} />
              ))}
            </TabList>

            <TabPanels>
              {slides.map((slide) => (
                <TabPanel key={slide.title} padding={0} paddingTop={2}>
                  <VStack textAlign="center">
                    <Text fontSize="20px" fontWeight={600}>
                      {slide.title}
                    </Text>

                    <Text fontSize="16px" fontWeight="600" color="neutral.600">
                      {slide.description}
                    </Text>

                    {slide.content}
                  </VStack>
                </TabPanel>
              ))}
            </TabPanels>
          </Tabs>
        </ModalBody>

        <ModalFooter display="flex" justifyContent="center">
          <ButtonGroup gap={4}>
            <Button variant="createCancel" onClick={() => navigate(-1)}>
              {t('global.actions.cancel')}
            </Button>

            <Button
              variant="solid"
              onClick={() => {
                if (tabIndex === slides.length - 1) {
                  openCreateInstanceModal();
                  onClose();
                } else {
                  handleTabsChange(tabIndex + 1);
                }
              }}
            >
              {t('admin.assignments.instances.welcomeModal.next')}
            </Button>
          </ButtonGroup>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
